import React, { useEffect, useState } from 'react';
import { InputMaskChangeEvent } from 'primereact/inputmask';
import UserListComponent from './UserListComponent';
import UserDialog from "./UserDialog";
import DeleteDialog from '../../DeleteDialog';

import { UserDTO, Userx } from '../../../DTO/Userx';
import { UserCrud } from '../../../utilities/UserCrud';
import {
    createUserxFromInterfaces,
    createUserxRoleArrayFromStrings,
} from '../../../factories/userxFactory';

const UserTable: React.FC = () => {
    const [users, setUsers] = useState<Userx[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);
    const [isNewUser, setIsNewUser] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await UserCrud.fetchAllUsers();
                setUsers(data.map((u) => createUserxFromInterfaces(u)));
            } catch {
                // handle
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const validateUser = (u: UserDTO | null) =>
        !!u && u.firstName && u.lastName && u.username && u.password;

    const handleSubmit = async () => {
        if (!validateUser(selectedUser)) return;
        if (isNewUser) {
            const newUser = await UserCrud.createUser(selectedUser!);
            setUsers((prev) => [...prev, newUser]);
        } else {
            const updated = await UserCrud.updateUser(selectedUser!);
            setUsers((prev) =>
                prev.map((u) => (u.id === updated.id ? updated : u))
            );
        }
        setDialogVisible(false);
    };

    const handleDelete = async () => {
        if (!selectedUser) return;
        await UserCrud.deleteUser(selectedUser);
        setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
        setDeleteDialogVisible(false);
    };

    const showNew = () => {
        setSelectedUser(Userx.empty());
        setIsNewUser(true);
        setDialogVisible(true);
    };
    const showEdit = (u: Userx) => {
        setSelectedUser(u);
        setIsNewUser(false);
        setDialogVisible(true);
    };
    const showDelete = (u: Userx) => {
        setSelectedUser(u);
        setDeleteDialogVisible(true);
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement> | InputMaskChangeEvent
    ) => {
        if (!selectedUser) return;
        const { name, value } = e.target;
        setSelectedUser({ ...selectedUser, [name]: value });
    };
    const handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedUser) return;
        const { name, checked } = e.target;
        setSelectedUser({ ...selectedUser, [name]: checked });
    };
    const handleRolesChange = (e: { value: string[] }) => {
        if (!selectedUser) return;
        setSelectedUser({
            ...selectedUser,
            roles: createUserxRoleArrayFromStrings(e.value),
        });
    };

    return (
        <div className="m-4 p-6 bg-surface border border-border rounded">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-primary">User List</h2>
                <button
                    onClick={showNew}
                    className="btn-primary"
                >
                    <i className="pi pi-plus mr-2" />
                    Add User
                </button>
            </div>

            <UserListComponent
                users={users}
                loading={loading}
                onEditUser={showEdit}
                onDeleteUser={showDelete}
            />

            <UserDialog
                visible={dialogVisible}
                user={selectedUser}
                isNewUser={isNewUser}
                onHide={() => setDialogVisible(false)}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                onRolesChange={handleRolesChange}
                onUserEnabledChange={handleEnabledChange}
            />

            <DeleteDialog
                visible={deleteDialogVisible}
                onHide={() => setDeleteDialogVisible(false)}
                onDelete={handleDelete}
                user={selectedUser}
            />
        </div>
    );
};

export default UserTable;
